import { ROOT_DOMAIN } from 'src/constants/environment';

const ANONYMOUS_ID_COOKIE = 'ajs_anonymous_id';
const ONE_YEAR_SECONDS = 60 * 60 * 24 * 365;

function readCookie(name: string): string | undefined {
    const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
    return match ? decodeURIComponent(match[1]) : undefined;
}

function writeCookie(name: string, value: string): void {
    const domain = ROOT_DOMAIN ? `; domain=${ROOT_DOMAIN}` : '';
    document.cookie = `${name}=${encodeURIComponent(
        value,
    )}; path=/; max-age=${ONE_YEAR_SECONDS}; samesite=lax${domain}`;
}

/**
 * Client-side replacement for the server-side `segment_anonymous_id_middleware`, which is
 * removed once the SPA is served statically (no Django on the shell request).
 *
 * Ensures a shared `ajs_anonymous_id` cookie (scoped to the root domain so it spans
 * subdomains) is set early — before Segment loads — from a `?anonymousId=` param (QR-scan
 * redirects), an existing cookie, or a freshly generated id. Mirrors the middleware's
 * precedence exactly. Safe to run while Django still serves the shell: it reads the
 * existing cookie first, so it won't fight the middleware-set value.
 */
export function ensureAnonymousIdCookie(): string {
    const fromQuery = new URLSearchParams(window.location.search).get('anonymousId');
    if (fromQuery) {
        writeCookie(ANONYMOUS_ID_COOKIE, fromQuery);
        return fromQuery;
    }

    const existing = readCookie(ANONYMOUS_ID_COOKIE);
    if (existing) {
        return existing;
    }

    const generated = crypto.randomUUID();
    writeCookie(ANONYMOUS_ID_COOKIE, generated);
    return generated;
}
